<template>
  <div class="i-upcoming-meetings">
    <div class="book">
      <div class="credit-deets">
        <div class="credit-card">
          <div class="credit">{{ userStore.getCoachingCredits }}</div>
        </div>
        <div class="deets">
          <div>Bookable Sessions Available</div>
          <div class="deet">
            <img :src="iconCalTiny" />You have {{ userStore.getCoachingCredits }} session credits left this month. Each
            credit is worth 30 minutes.
          </div>
        </div>
      </div>
      <iBaseButton
        v-bind="buttonStyle"
        :text="dynamicCopy.cta"
        :text-color="white"
        :icon-url="dynamicCopy.ctaIcon"
        :is-disabled="hasNoCreditsNoUpcomingSubscribed || (!hasCredits && isExecutiveOnce)"
        :background-image="hasNoCreditsNoUpcomingSubscribed ? bluePurpleGradientLight : bluePurpleGradient"
        padding="14px 24px"
        @clicked="dynamicCopy.ctaAction"
      />
    </div>

    <div class="core">
      <div
        v-if="!!data.bookings.length"
        class="bookings"
      >
        <div
          v-for="booking in data.bookings"
          :key="booking.id"
          class="booking"
        >
          <div
            v-if="isMdOrLarger"
            class="date"
          >
            <div class="when">
              {{ getWhen(booking.startTime) }}
            </div>
            <div>{{ new Date(booking.startTime).toLocaleString("en-US", localeDateStringOptions) }}</div>
          </div>
          <div class="card">
            <div class="info main">
              <div class="top">
                <div>
                  <span
                    v-if="!isMdOrLarger"
                    class="when"
                    >{{ getWhen(booking.startTime) + " " }}
                  </span>
                  <span v-if="!isMdOrLarger"
                    >{{ new Date(booking.startTime).toLocaleString("en-US", localeDateStringOptions) }} at</span
                  >
                  {{ new Date(booking.startTime).toLocaleString("en-US", localeTimeStringOptions) }}
                </div>
                <div
                  v-if="getTimeRemaining(booking.startTime, 'minute') <= 5"
                  class="badge"
                >
                  <div class="circle"></div>
                  {{
                    booking.status === CalcomStatus.cancelled
                      ? "CANCELLED"
                      : getTimeRemaining(booking.startTime, "minute") < 1
                        ? "ONGOING"
                        : `TODAY IN ${getTimeRemaining(booking.startTime, "minute")} MINS`
                  }}
                </div>
              </div>
              <div>{{ `${booking.length}-min ${booking.title}` }}</div>
              <div class="coach">
                <img
                  class="picture"
                  :src="assignedCoach.profilePhotoUrl || ''"
                />
                <div>With {{ assignedCoachFirstName }}</div>
              </div>
            </div>
            <div class="right">
              <div
                v-if="booking.status === CalcomStatus.cancelled"
                class="cancelled"
              >
                Cancelled
              </div>
              <iBaseButton
                v-else-if="getTimeRemaining(booking.startTime, 'minute') <= 5"
                v-bind="buttonStyle"
                text="Join Meeting"
                :text-color="azureRadiance700"
                :icon-url="iconZoom"
                @clicked="openUrl(booking.videoCallUrl)"
              />
              <iBaseButton
                v-else-if="getTimeRemaining(booking.startTime, 'minute') <= 1440"
                v-bind="buttonStyle"
                text="Cancel"
                :text-color="comet500"
                :icon-url="iconCancel"
                @clicked="openCancelModal(booking)"
              />
              <template v-else>
                <iBaseButton
                  v-bind="buttonStyle"
                  :text="
                    processingReschedule(booking.uid)
                      ? 'Rescheduling...'
                      : processingCancel(booking.uid)
                        ? 'Cancelling...'
                        : 'Reschedule'
                  "
                  :text-color="comet500"
                  :icon-url="iconReschedule"
                  @clicked="
                    processingReschedule(booking.uid) || processingCancel(booking.uid)
                      ? null
                      : getTimeRemaining(booking.startTime, 'minute') <= 1440
                        ? openRescheduleErrorModal(booking)
                        : openCalcomModal(
                            determineRescheduledEventType(booking.meetingType),
                            true,
                            booking.length,
                            booking.uid
                          )
                  "
                />
                <div
                  v-if="!processingReschedule(booking.uid) && !processingCancel(booking.uid)"
                  class="cancel-text"
                  @click="openCancelModal(booking)"
                >
                  Cancel meeting
                </div>
              </template>
            </div>
          </div>
        </div>
        <div class="note">
          All meetings links will be available 5 minutes before the meeting. Refresh this page if you do not see the
          Join link at that time.
        </div>
      </div>
      <div
        v-else
        class="empty"
      >
        <img :src="iconCal" />
        <div
          class="news"
          v-html="dynamicCopy.main"
        />
        <div>{{ dynamicCopy.sub }}</div>
      </div>
    </div>

    <div class="additional">
      <div class="extra">
        <div>Want an extra 30-minute session with your coach?</div>
        <div class="tiny">Book at least 24 hours in advance.</div>
      </div>
      <iBaseButton
        v-bind="buttonStyle"
        text="Buy an Extra Session"
        :text-color="azureRadiance700"
        :icon-url="iconCalBlue"
        version="outline"
        background-color="transparent"
        padding="12px 24px"
        @clicked="isExecutiveSubscribedOrCanceled || isExecutiveOnce ? buyExecExtraSession() : buyExtraSession()"
      />
    </div>

    <iBaseModal
      class="embed"
      :visible="data.showCancelModal"
      :fully-customisable="true"
      max-width="320px"
      @click-on-close="data.showCancelModal = false"
    >
      <div class="cancel-modal">
        <div class="cancel">Cancel Meeting</div>
        <div class="info">
          <div class="top">
            {{ new Date(data.cancelBooking?.startTime || "").toLocaleString("en-US", localeDateStringOptions) }} at
            {{ new Date(data.cancelBooking?.startTime || "").toLocaleString("en-US", localeTimeStringOptions) }}
          </div>
          <div>{{ `${data.cancelBooking?.length}-min ${data.cancelBooking?.title}` }}</div>
          <div class="coach">
            <img
              class="picture"
              :src="assignedCoach.profilePhotoUrl || ''"
            />
            <div>With {{ assignedCoachFirstName }}</div>
          </div>
        </div>
        <div class="warn">
          <template
            v-if="data.cancelBooking?.startTime && getTimeRemaining(data.cancelBooking?.startTime, 'minute') <= 1440"
          >
            <i class="ital"><strong>Heads-up!</strong> You're canceling less than 24 hours in advance.</i>
            <div class="grey">
              <div class="canc">
                This session can't be rescheduled and <span class="red">Canceling</span> now will forfeit the session
                credit.
              </div>
            </div>
          </template>
          <template v-else>
            <div class="grey">
              <div class="resch"><span class="blue">Heads Up!</span> Your coach's calendar is filling up fast.</div>
              <div>We recommend rescheduling instead.</div>
            </div>
          </template>
        </div>
        <div class="actions">
          <template
            v-if="data.cancelBooking?.startTime && getTimeRemaining(data.cancelBooking?.startTime, 'minute') <= 1440"
          >
            <iBaseButton
              v-bind="buttonStyle"
              text="Back"
              :text-color="azureRadiance700"
              version="outline"
              border-radius="8px"
              width="fit-content"
              @clicked="data.showCancelModal = false"
            />
            <iBaseButton
              v-bind="buttonStyle"
              text="Confirm Cancelation"
              :text-color="white"
              width="100%"
              :background-color="bittersweet500"
              border-radius="8px"
              @clicked="cancelBooking"
            />
          </template>
          <template v-else>
            <iBaseButton
              v-bind="buttonStyle"
              text="Cancel Meeting"
              :text-color="azureRadiance700"
              version="outline"
              border-radius="8px"
              width="100%"
              @clicked="cancelBooking"
            />
            <iBaseButton
              v-bind="buttonStyle"
              text="Reschedule"
              width="100%"
              :text-color="white"
              :background-image="bluePurpleGradient"
              border-radius="8px"
              @clicked="
                openCalcomModal(
                  determineRescheduledEventType(data.cancelBooking?.meetingType || ''),
                  true,
                  data.cancelBooking?.length,
                  data.cancelBooking?.uid
                )
              "
            />
          </template>
        </div>
      </div>
    </iBaseModal>
    <iBaseModal
      class="embed"
      :visible="data.showRescheduleErrorModal"
      :fully-customisable="true"
      max-width="320px"
      @click-on-close="data.showRescheduleErrorModal = false"
    >
      <div class="reschedule-error-modal">
        <div class="cancel red">Error Rescheduling!</div>
        <div class="info">
          <div class="top">
            {{ new Date(data.cancelBooking?.startTime || "").toLocaleString("en-US", localeDateStringOptions) }} at
            {{ new Date(data.cancelBooking?.startTime || "").toLocaleString("en-US", localeTimeStringOptions) }}
          </div>
          <div>{{ `${data.cancelBooking?.length}-min ${data.cancelBooking?.title}` }}</div>
          <div class="coach">
            <img
              class="picture"
              :src="assignedCoach.profilePhotoUrl || ''"
            />
            <div>With {{ assignedCoachFirstName }}</div>
          </div>
        </div>
        <div class="grey">
          This booking <span class="red">can no longer be rescheduled</span> because it’s less than 24 hours away.
        </div>
        <div class="actions">
          <iBaseButton
            v-bind="buttonStyle"
            text="Close"
            :text-color="azureRadiance700"
            version="outline"
            border-radius="8px"
            width="fit-content"
            @clicked="data.showRescheduleErrorModal = false"
          />
        </div>
      </div>
    </iBaseModal>
    <iBaseModal
      class="embed"
      :visible="data.showBookingSelectModal"
      :fully-customisable="true"
      max-width="320px"
      @click-on-close="data.showBookingSelectModal = false"
    >
      <div class="booking-select-modal">
        <div class="select">Book Your Next Session</div>
        <div class="options">
          <div class="title">Choose a session length:</div>
          <div
            class="option"
            :class="{ active: data.initialCoachingLength === 60 }"
            @click="data.initialCoachingLength = 60"
          >
            60-minutes
            <div class="creds">2-Session Credits</div>
          </div>
          <div
            class="option"
            :class="{ active: data.initialCoachingLength === 30 }"
            @click="data.initialCoachingLength = 30"
          >
            30-minutes
            <div class="creds">1-Session Credits</div>
          </div>
        </div>
        <div class="actions">
          <iBaseButton
            v-bind="buttonStyle"
            text="Bank"
            :text-color="azureRadiance700"
            version="outline"
            border-radius="8px"
            width="100%"
            @clicked="data.showBookingSelectModal = false"
          />
          <iBaseButton
            v-bind="buttonStyle"
            text="Schedule"
            width="100%"
            :text-color="white"
            border-radius="8px"
            :is-disabled="!data.initialCoachingLength"
            :background-image="!data.initialCoachingLength ? bluePurpleGradientLight : bluePurpleGradient"
            @clicked="
              data.initialCoachingLength
                ? openCalcomModal(
                    userStore.isCoachingExecutiveOrProfessionalSubscriber ? 'executive' : 'coaching',
                    false,
                    data.initialCoachingLength
                  )
                : null
            "
          />
        </div>
      </div>
    </iBaseModal>
    <iBaseModal
      class="embed"
      :visible="data.showCalcomEmbedModal"
      :fully-customisable="true"
      max-width="100%"
      @click-on-close="data.showCalcomEmbedModal = false"
    >
      <div class="booking-modal">
        <iCalcomScheduler
          :key="data.calcomEmbed.link"
          :on-booking-successful="firstBookingSuccess"
          :on-reschedule-successful="rescheduleSuccess"
          :embed-link="data.calcomEmbed.link"
          version="inline"
        />
      </div>
    </iBaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue"
import { useRouter } from "vue-router"
import { useUserStore } from "@/stores/user"
import { useCoachingStore } from "@/stores/coaching"
import {
  azureRadiance700,
  bittersweet500,
  bluePurpleGradient,
  bluePurpleGradientLight,
  comet500,
  white
} from "@/styles/colors"
import { breakpointSm, breakpointMd } from "@/styles/variables"

import getPaymentAction from "@/composables/getPaymentAction"
import useIsScreenSize from "@/composables/useIsScreenSize"
import { GlobalVariables } from "@/helpers/globalVariables"
import { GlobalFunctions } from "@/helpers/globalFunctions"
import api from "@/services/apiClient"
import UrlService from "@/services/urlService"
import { UserFlow } from "@/services/gtmService"

import iBaseModal from "@/components/base/BaseModal.vue"
import iBaseButton from "@/components/base/BaseButton.vue"
import iCalcomScheduler from "@/components/global/CalcomScheduler.vue"

// ASSETS
import iconCalendarDateWhite from "@/assets/icons/icon-calendar-date-white.svg"
import iconCalendarRepeatWhite from "@/assets/icons/icon-calendar-repeat-white.svg"
import iconCalendarPlusWhite from "@/assets/icons/icon-calendar-plus-white.svg"
import iconZoom from "@/assets/icons/icon-zoom.svg"
import iconCancel from "@/assets/icons/icon-slash-circle-grey.svg"
import iconReschedule from "@/assets/icons/icon-repeat-grey.svg"
import iconCal from "@/assets/icons/icon-calendar-plus-gradient.svg"
import iconCalBlue from "@/assets/icons/icon-calendar-plus-blue.svg"
import iconCalTiny from "@/assets/icons/icon-calendar-tiny.svg"
import iconLock from "@/assets/icons/icon-white-lock.svg"

// TYPES
import CalcomBooking from "@/shared/types/CalcomBooking"
import CalcomStatus from "@/shared/enums/CalcomStatus"
import StripePrice from "@/shared/enums/StripePrice"

// STORES, ROUTES AND COMPOSABLES
const userStore = useUserStore()
const coachingStore = useCoachingStore()
const isSmOrLarger = useIsScreenSize(breakpointSm)
const isMdOrLarger = useIsScreenSize(breakpointMd)
const router = useRouter()

// CONSTANTS
const localeDateStringOptions = {
  weekday: "long",
  month: "long",
  day: "numeric"
} as const
const localeTimeStringOptions = {
  timeZoneName: "short",
  hour: "numeric",
  minute: "numeric",
  hour12: true
} as const
const localeDateTimeStringOptions = {
  year: "numeric",
  month: "numeric",
  day: "numeric"
} as const

// DATA
const data = reactive({
  showCalcomEmbedModal: false,
  showCancelModal: false,
  showRescheduleErrorModal: false,
  showBookingSelectModal: false,
  showProcessing: false,
  showRescheduleProcessing: false,
  showCancelProcessing: false,
  rescheduleBookingUid: "" as string | undefined,
  initialCoachingLength: null as 60 | 30 | null,
  calcomEmbed: {
    link: "",
    type: "" as "coaching" | "executive" | "discovery",
    isReschedule: false
  },
  cancelBooking: null as CalcomBooking | null,
  bookings: [] as CalcomBooking[]
})

// COMPUTED
const processingReschedule = computed(
  () => (bookingUid: string) => data.showRescheduleProcessing && data.rescheduleBookingUid === bookingUid
)
const processingCancel = computed(
  () => (bookingUid: string) => data.showCancelProcessing && data.cancelBooking?.uid === bookingUid
)
const assignedCoach = computed(() => coachingStore.getAssignedCoach)
const assignedCoachFirstName = computed(() => assignedCoach.value?.fullName?.split(" ")[0])
const nextCoachingRenewalAt = computed(() =>
  userStore.userData.nextCoachingRenewalAt
    ? new Date(userStore.userData.nextCoachingRenewalAt)?.toLocaleString("en-US", localeDateTimeStringOptions)
    : null
)
const buttonStyle = computed(() => {
  return {
    backgroundColor: white,
    borderRadius: "6px",
    fontWeight: "500",
    width: isSmOrLarger.value ? "fit-content" : "100%",
    padding: "8px 12px",
    size: "small"
  }
})
const hasCredits = computed(() => !!userStore.getCoachingCredits)
const hasNoCreditsHasUpcoming = computed(() => !userStore.getCoachingCredits && !!data.bookings.length)
const hasNoCreditsNoUpcomingSubscribed = computed(
  () => !userStore.getCoachingCredits && !data.bookings.length && userStore.isCoachingSubscriber
)
const hasNoCreditsNotSubscribed = computed(() => !userStore.getCoachingCredits && !userStore.isCoachingSubscriber)
const isExecutiveOnce = computed(() => userStore.isCoachingOnce && userStore.hasPurchasedExecutiveDiscovery)
const isExecutiveSubscribedOrCanceled = computed(
  () => userStore.isCoachingExecutiveOrProfessionalSubscriber || userStore.hasPurchasedExecutiveSubscription
)
const dynamicCopy = computed(() => {
  return {
    main: hasCredits.value
      ? "<span>You have no scheduled upcoming meetings.</span>"
      : hasNoCreditsNoUpcomingSubscribed.value
        ? `<span>Good news!</span><span>Your subscription renews ${nextCoachingRenewalAt.value ? `on ${nextCoachingRenewalAt.value}` : "soon"}.</span>`
        : "<span>You have no more available sessions.</span>",
    sub: hasCredits.value
      ? "Book your next meeting with your coach from the section above and it will show up here!"
      : hasNoCreditsNoUpcomingSubscribed.value
        ? `You'll be able to book your next session ${nextCoachingRenewalAt.value ? "then" : "when it renews. Contact support to find out exactly when"}.`
        : `${userStore.isCoachingCancelled ? "Res" : "S"}ubscribe to more sessions with your coach ${isExecutiveOnce.value ? "via the purchase link they`ve provided you" : "from the section above"} and you can book more meetings!`,
    cta: data.showProcessing
      ? "Processing..."
      : // If any users have credits, they can book.
        // If no credits but is a ONCE user that purchased executive discovery, they will see this text on a disabled button.
        hasCredits.value || isExecutiveOnce.value
        ? "Book your next session"
        : // If they have upcoming meetings but no credits they should be able to buy an on-demand session.
          hasNoCreditsHasUpcoming.value
          ? "Buy an Extra Session"
          : // Regular and executive subscribers will see this if they have no upcoming bookings.
            hasNoCreditsNoUpcomingSubscribed.value
            ? `Booking opens ${nextCoachingRenewalAt.value || "soon"}`
            : // Only regular once users will see this as executive once users got filtered out at the first logic statement.
              userStore.isCoachingOnce
              ? "Subscribe"
              : // CANCELLED regular and executive users will see this
                "Resubscribe",
    ctaIcon: data.showProcessing
      ? ""
      : // Same logic from cta above applies here.
        hasCredits.value || isExecutiveOnce.value
        ? iconCalendarDateWhite
        : hasNoCreditsHasUpcoming.value
          ? iconCalendarPlusWhite
          : hasNoCreditsNoUpcomingSubscribed.value
            ? iconLock
            : hasNoCreditsNotSubscribed.value
              ? iconCalendarRepeatWhite
              : "",
    ctaAction: () => {
      return data.showProcessing
        ? null
        : hasCredits.value
          ? userStore.getCoachingCredits < 2
            ? openCalcomModal(
                userStore.isCoachingExecutiveOrProfessionalSubscriber ? "executive" : "coaching",
                false,
                30
              )
            : openBookingSelectModal()
          : isExecutiveOnce.value
            ? null
            : hasNoCreditsHasUpcoming.value
              ? isExecutiveSubscribedOrCanceled.value
                ? buyExecExtraSession()
                : buyExtraSession()
              : hasNoCreditsNotSubscribed.value
                ? goToSelectPlan()
                : null
    }
  }
})

// FUNCTIONS
/**
 * Fetches a list of upcoming/ongoing paid bookings for this user
 */
const fetchUpcomingOngoingPaidBooking = async () => {
  try {
    const bookings = await api.booking.getUpcomingPaidList({
      bookingEventTypes: ["discovery", "coaching", "executiveCoaching", "onDemand"]
    })
    data.bookings = bookings.bookings
    await userStore.loadUserData()
  } catch (error) {
    console.error("Error fetching user's upcoming/ongoing paid bookings:", error)
  }
}

const openCancelModal = (cancelBooking: CalcomBooking) => {
  data.cancelBooking = cancelBooking
  data.showCancelModal = true
}

const openRescheduleErrorModal = (cancelBooking: CalcomBooking) => {
  data.cancelBooking = cancelBooking
  data.showRescheduleErrorModal = true
}

const cancelBooking = async () => {
  data.showCancelProcessing = true
  try {
    if (data.cancelBooking) {
      await api.booking.cancel(data.cancelBooking.calcomId)
    }
  } catch (error) {
    console.error("Error cancelling this booking", error)
  } finally {
    data.showCancelModal = false
    setTimeout(async () => {
      await fetchUpcomingOngoingPaidBooking()
      data.showCancelProcessing = false
    }, 1700)
  }
}

const openBookingSelectModal = () => {
  data.initialCoachingLength = null
  data.showBookingSelectModal = true
}

const getTimeRemaining = (bookingStartTime: Date, type: "day" | "minute") => {
  return GlobalFunctions.calculateDateDifference(new Date(), new Date(bookingStartTime))[type]
}

const getWhen = (bookingStartTime: Date) => {
  const dayDiffrence = getTimeRemaining(bookingStartTime, "day")

  return dayDiffrence < 1 ? "Today" : dayDiffrence < 2 ? "Tomorrow" : `In ${dayDiffrence} days`
}

const determineRescheduledEventType = (meetingType: string) => {
  switch (meetingType) {
    case "discovery":
      return "discovery"
    case "executiveCoaching":
      return "executive"
    default:
      return "coaching"
  }
}

const firePaymentAction = async (stripePrice: StripePrice, userFlow: UserFlow) => {
  const stripePrices = [stripePrice]
  const paymentAction = getPaymentAction(stripePrices, userFlow)

  try {
    await paymentAction()
  } catch (error) {
    console.error(`Failed to create checkout session for ${stripePrice}`, error)
  }
}

const bookingSuccessful = async () => {
  data.showCalcomEmbedModal = false
  setTimeout(async () => {
    await fetchUpcomingOngoingPaidBooking()
    data.showProcessing = false
    data.showRescheduleProcessing = false
  }, 1700)
}
const firstBookingSuccess = async () => {
  data.showProcessing = true
  await bookingSuccessful()
}
const rescheduleSuccess = async () => {
  data.showRescheduleProcessing = true
  await bookingSuccessful()
}

const buyExtraSession = async () => {
  firePaymentAction(StripePrice.COACHING_EXTRA_SESSION_HIGH, UserFlow.COACHING)
}

const buyExecExtraSession = async () => {
  firePaymentAction(StripePrice.COACHING_EXECUTIVE_EXTRA_SESSION, UserFlow.COACHING)
}

const openCalcomModal = (
  type: "coaching" | "executive" | "discovery",
  isReschedule = false,
  length = 30,
  bookingUid?: string
) => {
  data.showCancelModal = false
  data.showBookingSelectModal = false
  const calcomUsername = assignedCoach.value.calcomUsername
  if (!calcomUsername) return
  data.calcomEmbed.link = UrlService.generateCalComEmbedLink(
    `${calcomUsername}/${type}-${length}${isReschedule ? `?rescheduleUid=${bookingUid}` : ""}`
  )
  data.calcomEmbed.type = type
  data.calcomEmbed.isReschedule = isReschedule
  if (isReschedule) data.rescheduleBookingUid = bookingUid
  data.showCalcomEmbedModal = true
}

const openUrl = (redirectUrl: string) => {
  window.open(redirectUrl, "_blank")
}

const goToSelectPlan = () => {
  router.push({
    name: userStore.isExecutiveCoachingPotential
      ? GlobalVariables.urls.coaching.children.choosePlan.routeName
      : GlobalVariables.urls.coaching.children.selectPlan.routeName
  })
}

// LIFECYCLE HOOKS
onMounted(async () => {
  await fetchUpcomingOngoingPaidBooking()
})
</script>

<style lang="scss" scoped>
.i-upcoming-meetings {
  @include flex(column, start, stretch, $gap: 25px);

  .book {
    @include flex(column, start, center, $gap: 14px);
    @include display-xs;
    @include border-radius-md;

    padding: 10px;
    font-weight: 700;
    color: $black;
    background: linear-gradient(90deg, #f1dbff80 -1.11%, #dcddfa80 99.93%);

    @media screen and (min-width: $breakpoint-sm) {
      padding: 20px;
      gap: 20px;
    }

    @media screen and (min-width: $breakpoint-md) {
      @include flex(row, space-between, center, $gap: 10px);
    }

    .credit-deets {
      @include flex(row, start, center, $gap: 18px);

      .credit-card {
        @include border-radius-sm;

        padding: 1px;
        background-image: $blue-purple-gradient;

        .credit {
          @include flex(column, center, center);

          border-radius: 7px;
          height: 48px;
          width: 48px;
          background-color: $white;
        }
      }

      .deets {
        @include flex(column, start, start, $gap: 2px);
        @include text-sm;

        .deet {
          @include flex(row, start, center, $gap: 4px);
          @include text-xxs;

          color: $comet700;
          font-weight: 400;
        }
      }
    }
  }

  .core {
    .bookings {
      @include flex(column, start, stretch);

      .booking {
        @include grid($cols: 1fr, $rows: auto, $align-items: start, $gap: 17px);

        @media screen and (min-width: $breakpoint-md) {
          @include grid($cols: 160px 1fr, $rows: auto, $align-items: start, $gap: 17px);
        }

        .date {
          @include text-xs;

          color: $comet800;
          font-weight: 400;
          border-right: 2px solid #e8e8e8;
          height: 100%;
          padding: 12px 10px 0 0;
        }

        .card {
          @include flex(column, start, stretch, $gap: 18px);
          @include border-radius-lg;

          padding: 16px;
          background-color: $black50;
          margin: 10px 0;

          @media screen and (min-width: $breakpoint-sm) {
            @include flex(row, space-between, center, $gap: 10px);

            padding: 16px 20px;
          }

          @media screen and (min-width: $breakpoint-md) {
            padding: 16px 24px;
          }

          .right {
            @include flex(column, center, center, $gap: 4px);
            @include border-radius-lg;

            .cancelled {
              @include text-xs;

              color: $bittersweet600;
            }

            .cancel-text {
              @include text-xxs;

              color: $black300;
              cursor: pointer;
              text-decoration: underline;

              &:hover {
                color: $black400;
              }
            }
          }
        }
      }

      .note {
        @include flex(row, end, center);
        @include text-xs;

        color: $black500;
        font-weight: 500;
      }
    }

    .empty {
      @include flex(column, center, center, $gap: 14px);
      @include border-radius-md;
      @include text-sm;

      padding: 12px;
      border: 1px solid $black50;
      color: $black;
      text-align: center;

      @media screen and (min-width: $breakpoint-sm) {
        padding: 24px;
        gap: 22px;
      }

      .news {
        @include flex(column, center, center);

        font-weight: 700;
      }
    }
  }

  .info {
    @include flex(column, start, start);
    @include text-xl;

    color: $black;
    font-weight: 600;

    &.main {
      gap: 8px;
      flex: 1 0 min-content;
    }

    .top {
      @include flex(row, start, center, $gap: 15px);
      @include text-xs;

      color: $comet800;
      font-weight: 400;

      .badge {
        @include flex(row, center, center, $gap: 8px);
        @include border-radius-md;
        @include text-xxs;

        width: fit-content;
        background-image: $blue-purple-gradient;
        padding: 3px 8px;
        color: $white;

        .circle {
          @include border-radius-rounded;

          width: 4px;
          height: 4px;
          flex: 0 0 4px;
          background-color: white;
        }
      }
    }

    .coach {
      @include flex(row, start, center, $gap: 6px);
      @include text-xs;

      color: $comet800;
      font-weight: 400;

      .picture {
        @include border-radius-rounded;

        width: 16px;
        height: 16px;
        flex: 0 0 16px;
      }
    }
  }

  .when {
    @include text-sm;

    color: $black;
    font-weight: 700;
  }

  .additional {
    @include flex(column, start, center, $gap: 12px);
    @include text-xl;
    @include border-radius-md;

    padding: 10px;
    font-weight: 600;
    color: $black;
    background-color: $black50;
    text-align: center;

    @media screen and (min-width: $breakpoint-sm) {
      padding: 20px;
      gap: 20px;
    }

    @media screen and (min-width: $breakpoint-md) {
      @include flex(row, space-between, center, $gap: 10px);

      text-align: start;
    }

    .extra {
      @include flex(column, center, center);

      flex: 1 0 min-content;

      @media screen and (min-width: $breakpoint-md) {
        align-items: start;
      }

      .tiny {
        @include text-xs;

        color: $comet800;
        font-weight: 400;
      }
    }
  }

  .embed {
    padding: 30px 0;

    @media screen and (min-width: $breakpoint-lg) {
      padding: unset;
    }

    .booking-modal {
      @include flex($direction: column, $justify: start, $align: center);

      margin: 10px;

      @media screen and (min-width: $breakpoint-md) {
        margin: 25px;
      }
    }

    .cancel-modal {
      @include flex(column, start, stretch, $gap: 14px);

      padding: 20px;

      .cancel {
        @include text-xl;

        color: $azure-radiance700;
        font-weight: 600;
      }

      .warn {
        @include flex(column, center, center, $gap: 12px);
        @include text-xs;

        text-align: center;

        .ital {
          font-weight: 400;
          text-align: center;
        }

        .grey {
          @include flex(column, center, center, $gap: 8px);
          @include border-radius-sm;

          color: $comet800;
          padding: 16px 18px;
          background-color: $black50;

          .resch {
            @include flex(column, center, center);

            font-weight: 700;
          }

          .canc {
            font-weight: 700;
          }

          .red {
            color: $bittersweet700;
          }

          .blue {
            color: $azure-radiance600;
          }
        }
      }

      .actions {
        @include flex(row, space-between, center, $gap: 10px);
      }
    }

    .reschedule-error-modal {
      @include flex(column, start, stretch, $gap: 14px);

      padding: 20px;

      .red {
        color: $bittersweet700;
      }

      .cancel {
        @include text-xl;

        color: $bittersweet600;
        font-weight: 600;
      }

      .grey {
        @include border-radius-sm;
        @include text-xs;

        text-align: center;
        color: $comet800;
        font-weight: 700;
        padding: 16px 18px;
        background-color: $black50;
      }

      .actions {
        @include flex(row, center, center);
      }
    }

    .booking-select-modal {
      @include flex(column, start, stretch, $gap: 20px);

      padding: 20px;

      .select {
        @include text-xl;

        color: $azure-radiance700;
        font-weight: 600;
      }

      .options {
        @include flex(column, start, stretch, $gap: 10px);

        .title {
          @include text-sm;

          font-weight: 700;
          color: $comet800;
        }

        .option {
          @include flex(row, space-between, center, $gap: 16px);
          @include border-radius-sm;
          @include text-xl;

          border: 1px solid $black100;
          background-color: $white;
          font-weight: 700;
          padding: 6px 12px;
          cursor: pointer;

          &:hover,
          &.active {
            background-color: $azure-radiance50;
          }

          .creds {
            @include text-xs;

            font-weight: 500;
            color: $comet800;
          }
        }
      }

      .actions {
        @include flex(row, space-between, center, $gap: 10px);
      }
    }
  }
}
</style>
